import { Point, Tooltip, TooltipOptions } from './types';

const defaultValueFormatter = (x: string | number) => x;

const numberToString = (num: number | string) =>
  Number(num).toFixed(2).replace(/(0+)$/, '').replace(/\.$/, '');

const getFormattedValue = (value: string | number, tooltipOptions?: TooltipOptions) => {
  const stringValue = value ? numberToString(value) : value;

  if (!tooltipOptions) {
    return stringValue;
  }

  const valueFormatter = tooltipOptions.valueFormatter
    ? tooltipOptions.valueFormatter
    : defaultValueFormatter;

  return (
    (tooltipOptions.valuePrefix || '') +
    valueFormatter(stringValue) +
    (tooltipOptions.valueSuffix || '')
  );
};

export function graphicFormatter(): string {
  return `
    <table>
      <tr class="highcharts-header"><th>${this.x}</th></tr>
      ${this.points.reduce(
        (acc: string, point: Point) => `${acc}
          <tr>
            <td>
              <span class="highcharts-color-${point.colorIndex}">●</span>
              <b>${point.series.name}</b>
              <!-- Add space between name and value
                   values will be outside tooltip box if using padding -->
              &nbsp;&nbsp;
            </td>
            <td class="highcharts-tooltip-value">${getFormattedValue(
              point.y,
              point.series.tooltipOptions
            )}</td>
          </tr>
          `,
        ''
      )}
    </table>
  `;
}

function singleBarFormatter(tooltip: Tooltip) {
  const point = this.points[0];

  const percentage = (this.y / tooltip.options.total) * 100;

  return `
    <table>
      <tr class="highcharts-header"><th>${tooltip.options.header}</th></tr>
      <tr>
        <td><b>${this.x}&nbsp;&nbsp;</b></td>
      </tr>
      <tr>
        <td class="highcharts-tooltip-value highcharts-tooltip-value--single">
          <span class="highcharts-color-0">●</span>
          ${getFormattedValue(point.y, tooltip.options)}
          <span class="highcharts-tooltip-gray">
            (${Number(percentage).toFixed(2)}%)
          </span>
        </td>
      </tr>
    </table>
  `;
}

function multipleBarFormatter(tooltip: Tooltip) {
  const total = this.points.reduce((acc: string, point: Point) => acc + point.y, 0);

  return `
    <table>
      <tr class="highcharts-header"><th>${tooltip.options.header}</th></tr>
      <tr>
        <td><b>${this.x}&nbsp;&nbsp;</b></td>
        <td class="highcharts-tooltip-value"><b>${getFormattedValue(
          total,
          tooltip.options
        )}</b></td>
      </tr>
      ${this.points.reduce(
        (acc: string, point: Point) => `${acc}
          <tr>
            <td>
              <span class="highcharts-color-${point.colorIndex}">●</span>
              ${point.series.name}
              <!-- Add space between name and value
                   values will be outside tooltip box if using padding -->
              &nbsp;&nbsp;
            </td>
            <td class="highcharts-tooltip-value">${getFormattedValue(
              point.y,
              point.series.tooltipOptions
            )}</td>
          </tr>
          `,
        ''
      )}
    </table>
  `;
}

export function barFormatter(tooltip: Tooltip): string {
  const isSingleData = this.points.length === 1;

  return isSingleData
    ? singleBarFormatter.call(this, tooltip)
    : multipleBarFormatter.call(this, tooltip);
}

export function pieFormatter(): string {
  return `
    <table>
      <tr class="highcharts-header"><th>${this.series.name}</th></tr>
      <tr>
        <td>
          <span class="highcharts-color-${this.colorIndex}">●</span>
          <b>${this.key}</b>
        </td>
      </tr>
      <tr>
        <td>
          ${getFormattedValue(this.point.y, this.series.tooltipOptions)}
          <span class="highcharts-tooltip-gray">
            (${Number(this.point.percentage).toFixed(2)}%)
          </span>
        </td>
      </tr>
    </table>
  `;
}

export function mapFormatter(tooltip: Tooltip): string {
  return `
    <table>
      <tr class="highcharts-header">
        <th colspan="2">${tooltip.options.header}</th>
      </tr>
      <tr>
        <td>
          <span class="highcharts-color-0">●</span>
          <b>${this.point.options.name || this.key}</b>
          &nbsp;&nbsp;
        </td>
        <td class="highcharts-tooltip-value">${getFormattedValue(
          this.point.value,
          tooltip.options
        )}</td>
      </tr>
    </table>
  `;
}
